import { RevealSensitiveButton } from '../components/RevealSensitiveButton'
import { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { type LoginResponse } from '../api/auth'
import { companiesApi, type CompanyDetail } from '../api/companies'
import { MoveToTrashButton, TrashBanner } from '../components/TrashControls'
import {
  FormGrid,
  Button,
  ButtonLink,
  Callout,
  Card,
  CardContent,
  CardHeader,
  EmptyState,
  LoadingState,
  Page as PageShell,
  PageHeader,
  PaneLayout,
  CardGrid,
  KeyValueList,
  StatusPill,
  TextField,
  type PillTone,
} from '../components/ui'

function formatDate(s: string): string {
  return new Date(s).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function statusBadge(status: string) {
  const map: Record<string, { tone: PillTone; label: string }> = {
    active: { tone: 'success', label: 'Attiva' },
    inactive: { tone: 'warning', label: 'Inattiva' },
    suspended: { tone: 'error', label: 'Sospesa' },
    merged: { tone: 'neutral', label: 'Unita' },
  }
  const entry = map[status] ?? {
    tone: 'neutral' as PillTone,
    label: status.charAt(0).toUpperCase() + status.slice(1),
  }
  return <StatusPill tone={entry.tone}>{entry.label}</StatusPill>
}

/** Same Admin-tier check Dashboard.tsx uses for its Admin-only stats row (`auth.role ===
 * 'Administrator' || auth.role === 'EqualAdmin'`) — the two role values `PolicyNames.Admin`
 * grants server-side. `CustomerDetailPage.tsx` has no Admin-gated action of its own to model
 * (it doesn't expose merge at all), so this reuses the one real precedent for role-gating a UI
 * element in this codebase instead of inventing a new mechanism. */
function isAdmin(auth: LoginResponse): boolean {
  return auth.role === 'Administrator' || auth.role === 'EqualAdmin'
}

interface Props {
  auth: LoginResponse
}

/** Admin-only merge panel: `POST /api/companies/{id}/merge` is `[Authorize(PolicyNames.Admin)]`
 * on the backend (`CompaniesController.cs`), consolidating the company named here (loser) into
 * the one being viewed (winner). Visibility of the whole card, not just the button, is gated —
 * a non-Admin should not even learn the action exists. */
function MergePanel({ winnerId, onMerged }: { winnerId: number; onMerged: () => void }) {
  const [open, setOpen] = useState(false)
  const [loserId, setLoserId] = useState('')
  const [motivation, setMotivation] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<string | null>(null)

  async function handleMerge(e: React.FormEvent) {
    e.preventDefault()
    const loserCompanyId = parseInt(loserId, 10)
    if (isNaN(loserCompanyId)) {
      setError('Inserisci un ID azienda valido.')
      return
    }
    setSaving(true)
    setError(null)
    setResult(null)
    try {
      const res = await companiesApi.merge(winnerId, loserCompanyId, motivation || undefined)
      setResult(
        `Azienda #${res.loserCompanyId} unita in #${res.winnerCompanyId}. ` +
          `Campi riempiti dal record perdente: ${res.fieldsFilledFromLoser.length > 0 ? res.fieldsFilledFromLoser.join(', ') : 'nessuno'}.`,
      )
      setLoserId('')
      setMotivation('')
      onMerged()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore durante il merge.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card variant="outlined">
      <CardHeader
        icon="merge_type"
        title="Unisci azienda"
        actions={
          !open && (
            <Button variant="outlined" size="sm" onClick={() => setOpen(true)}>
              Unisci
            </Button>
          )
        }
      />
      {open && (
        <CardContent>
          {result && <Callout tone="success">{result}</Callout>}
          {error && <Callout tone="error">{error}</Callout>}
          <form onSubmit={e => void handleMerge(e)}>
            <FormGrid columns={4} className="md-form-grid--end">
              <div>
                <TextField
                  id="merge-loser-id"
                  label="ID azienda da unire (perdente)"
                  type="number"
                  value={loserId}
                  onChange={e => setLoserId(e.target.value)}
                  required
                />
              </div>
              <div className="md-span-2">
                <TextField
                  id="merge-motivation"
                  label="Motivazione (opzionale)"
                  type="text"
                  value={motivation}
                  onChange={e => setMotivation(e.target.value)}
                />
              </div>
              <div className="d-flex gap-2">
                <Button type="button" variant="text" size="sm" disabled={saving} onClick={() => setOpen(false)}>
                  Chiudi
                </Button>
                <Button type="submit" variant="text" tone="error" size="sm" disabled={saving}>
                  {saving ? 'Unione...' : 'Conferma'}
                </Button>
              </div>
            </FormGrid>
          </form>
        </CardContent>
      )}
    </Card>
  )
}

export function CompanyDetailPage({ auth }: Props) {
  const { id: idStr } = useParams<{ id: string }>()
  const id = parseInt(idStr ?? '', 10)

  const [company, setCompany] = useState<CompanyDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notFound, setNotFound] = useState(false)

  // `silent` skips the full-page spinner — used to refresh the winner's data after a merge
  // without unmounting `MergePanel` (and its just-shown success message) underneath it.
  const load = (silent = false) => {
    if (isNaN(id)) {
      setError('ID azienda non valido.')
      setLoading(false)
      return
    }
    if (!silent) setLoading(true)
    setError(null)
    setNotFound(false)
    companiesApi
      .get(id)
      .then(data => setCompany(data))
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err)
        if (msg.includes('404') || msg.toLowerCase().includes('not found')) {
          setNotFound(true)
        } else {
          setError(msg || "Errore nel caricamento dell'azienda.")
        }
      })
      .finally(() => { if (!silent) setLoading(false) })
  }

  useEffect(() => load(), [id])

  if (loading) {
    return <LoadingState />
  }

  if (notFound) {
    return (
      <Callout tone="warning">
        Azienda non trovata.{' '}
        <Link to="/companies">Torna alla lista</Link>
      </Callout>
    )
  }

  if (error) {
    return <Callout tone="error">{error}</Callout>
  }

  if (!company) return null

  const c = company

  return (
    <PageShell>
      <PageHeader
        back={{ to: '/companies', label: 'Aziende' }}
        icon="business"
        title={c.name}
        subtitle={statusBadge(c.status)}
        actions={
          <>
            <RevealSensitiveButton entityType="companies" entityId={c.id} values={[c.phone, c.fiscalCode]} onRevealed={() => load(true)} />
            <ButtonLink to={`/companies/${c.id}/edit`} variant="outlined" icon="edit">
              Modifica
            </ButtonLink>
            {isAdmin(auth) && !c.deletedAt && (
              <MoveToTrashButton kind="companies" id={c.id} onMoved={() => load(true)} />
            )}
          </>
        }
      />

      {c.deletedAt && (
        <TrashBanner
          kind="companies"
          id={c.id}
          deletedAt={c.deletedAt}
          reason={c.deleteReason}
          canRestore={isAdmin(auth)}
          onRestored={() => load(true)}
        />
      )}

      {c.mergedIntoCompanyId != null && (
        <Callout tone="info">
          Questa azienda è stata unita nell'azienda #{c.mergedIntoCompanyId}
          {c.mergedAt ? ` il ${formatDate(c.mergedAt)}` : ''}.
          {c.canonicalCompanyId != null && c.canonicalCompanyId !== c.mergedIntoCompanyId && (
            <>
              {' '}Il record vivo attuale è{' '}
              <Link to={`/companies/${c.canonicalCompanyId}`}>#{c.canonicalCompanyId}</Link>.
            </>
          )}
        </Callout>
      )}

      {/* Body */}
      <PaneLayout
        variant="supporting"
        main={
          <>
            {/* Left: anagrafica */}
            <Card variant="outlined">
              <CardHeader icon="business" title="Anagrafica" />
              <CardContent>
                <KeyValueList columns={2}
                  items={[
                    { label: 'Ragione Sociale', value: <>{c.name || '—'}</> },
                    { label: 'Email', value: <>{c.email ? <a href={`mailto:${c.email}`}>{c.email}</a> : '—'}</> },
                    { label: 'Telefono', value: <>{c.phone ? <a href={`tel:${c.phone}`}>{c.phone}</a> : '—'}</> },
                    { label: 'Città', value: <>{c.city ? (c.province ? `${c.city} (${c.province})` : c.city) : '—'}</> },
                    { label: 'Indirizzo', value: <>{c.address || '—'}</>, full: true },
                    { label: 'Codice fiscale', value: <>{c.fiscalCode || '—'}</> },
                    { label: 'P.IVA', value: <>{c.vatNumber || '—'}</> },
                    { label: 'PEC', value: <>{c.pec || '—'}</> },
                    { label: 'Note', value: <><span className="text-muted md-typescale-body-small">{c.notes || '—'}</span></>, full: true },
                  ]}
                />
              </CardContent>
            </Card>

            {isAdmin(auth) && <MergePanel winnerId={c.id} onMerged={() => load(true)} />}
          </>
        }
        pane={
          <>
            {/* Right: riepilogo */}
            <Card variant="outlined">
              <CardContent>
                <CardGrid min={200} gap="collection" className="text-center">
                  <Card as="div" variant="filled" padding="sm" className="gap-1">
                    <div className="md-typescale-body-small text-muted">Codice</div>
                    <strong>{c.code}</strong>
                  </Card>
                  <Card as="div" variant="filled" padding="sm" className="gap-1">
                    <div className="md-typescale-body-small text-muted">Registrata il</div>
                    <strong>{formatDate(c.createdAt)}</strong>
                  </Card>
                </CardGrid>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardHeader icon="description" title="Contratti" />
              <CardContent>
                <EmptyState icon="description" title="Nessun contratto associato" compact />
              </CardContent>
            </Card>
          </>
        }
      />
    </PageShell>
  )
}
